// src/components/Header.tsx
import React, { ChangeEvent, RefObject, useState, useEffect, useRef, useCallback } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom'; // Import useLocation
import styles from './Header.module.css';
import type { SavedLink } from './LinkSaver';
import type { User } from 'firebase/auth';

// --- SVG Icons --- (Bạn có thể đặt chúng ở đây hoặc import từ file riêng)
const HOME_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="${styles.navIcon}"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>`;
const PUBLIC_LINKS_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="${styles.navIcon}"><circle cx="12" cy="12" r="10"></circle><line x1="2" y1="12" x2="22" y2="12"></line><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path></svg>`;
const ADMIN_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="${styles.navIcon}"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="9" y1="3" x2="9" y2="21"></line><line x1="15" y1="3" x2="15" y2="21"></line><line x1="3" y1="9" x2="21" y2="9"></line><line x1="3" y1="15" x2="21" y2="15"></line></svg>`; // Example Admin Icon

const FILTER_ICON_SVG = `<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M7 11H17V13H7V11ZM4 7H20V9H4V7ZM10 15H14V17H10V15Z"/></svg>`; // Filter icon, stroke removed for fill
const GOOGLE_ICON_SVG = `<svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 488 512"><path fill="currentColor" d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 123 24.5 166.3 64.9l-67.5 64.9C258.5 52.6 94.3 116.6 94.3 256c0 86.5 69.1 156.6 153.7 156.6 98.2 0 135-70.4 140.8-106.9H248v-85.3h236.1c2.3 12.7 3.9 24.9 3.9 41.4z"></path></svg>`;

export interface NavLinkItem {
  to: string;
  label: string;
  show?: boolean;
  iconSVG?: string; // Thêm prop iconSVG
}

export interface HeaderProps {
  appName?: string;
  logoUrl?: string;
  navLinks?: NavLinkItem[];
  allLinksForSuggestions: Pick<SavedLink, 'id' | 'title' | 'url' | 'thumbnailUrl'>[];
  onSearchQueryChange: (query: string) => void;
  onToggleFilterPanel: () => void;
  currentUser: User | null;
  onSignIn: () => void;
  onSignOut: () => void;
}

const Header: React.FC<HeaderProps> = ({
  appName = "Trình Lưu Link",
  logoUrl = '/app-header-logo.png',
  navLinks = [],
  allLinksForSuggestions,
  onSearchQueryChange,
  onToggleFilterPanel,
  currentUser,
  onSignIn,
  onSignOut,
}) => {
  const [internalSearchQuery, setInternalSearchQuery] = useState<string>("");
  const [internalTitleSuggestions, setInternalTitleSuggestions] = useState<Pick<SavedLink, 'id' | 'title'>[]>([]);
  const [internalShowSuggestions, setInternalShowSuggestions] = useState<boolean>(false);
  const internalSuggestionsRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const location = useLocation(); // Get current location

  // Gán icon cho navLinks nếu chưa có
  const processedNavLinks = navLinks.map(link => {
    if (link.iconSVG) return link; // Nếu đã có icon thì giữ nguyên
    if (link.to === "/") return { ...link, iconSVG: HOME_ICON_SVG };
    if (link.to === "/public") return { ...link, iconSVG: PUBLIC_LINKS_ICON_SVG };
    if (link.to === "/admin") return { ...link, iconSVG: ADMIN_ICON_SVG };
    return link;
  });

  const actualLogoUrl = logoUrl.includes('?') ? logoUrl : `${logoUrl}?t=${new Date().getTime()}`;

  const handleInternalSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setInternalSearchQuery(query);
    onSearchQueryChange(query);

    if (query.trim() !== "") {
      const suggestions = allLinksForSuggestions
        .filter(link => link.title.toLowerCase().includes(query.toLowerCase()))
        .map(link => ({ id: link.id, title: link.title }))
        .slice(0, 7);
      setInternalTitleSuggestions(suggestions);
      setInternalShowSuggestions(true);
    } else {
      setInternalTitleSuggestions([]);
      setInternalShowSuggestions(false);
    }
  };

  const handleInternalSuggestionClick = (title: string) => {
    setInternalSearchQuery(title);
    onSearchQueryChange(title); // Make sure this updates the parent's query
    setInternalTitleSuggestions([]);
    setInternalShowSuggestions(false);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleInternalSearchFocus = () => {
    if (internalSearchQuery.trim() !== "" && internalTitleSuggestions.length > 0) {
      setInternalShowSuggestions(true);
    }
  };

  const handleClickOutsideSuggestions = useCallback((event: MouseEvent) => {
    const targetNode = event.target as Node;
    if (
      internalSuggestionsRef.current &&
      !internalSuggestionsRef.current.contains(targetNode) &&
      searchInputRef.current &&
      !searchInputRef.current.contains(targetNode)
    ) {
      setInternalShowSuggestions(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutsideSuggestions);
    return () => {
      document.removeEventListener("mousedown", handleClickOutsideSuggestions);
    };
  }, [handleClickOutsideSuggestions]);

  return (
    <header className={styles.header}>
      <div className={styles.leftSection}>
        {logoUrl && <img src={actualLogoUrl} alt={`${appName} Logo`} className={styles.logoImage} />}
        <span className={styles.appName}>{appName}</span>
        <nav className={styles.navigation}>
          {processedNavLinks.map(link => ( // Sử dụng processedNavLinks
            (typeof link.show === 'undefined' || link.show) && (
              <RouterLink 
                key={link.to} 
                to={link.to} 
                className={`${styles.navLink} ${location.pathname === link.to ? styles.navLinkActive : ''}`}
                title={link.label} // Thêm title cho tooltip
              >
                {link.iconSVG && <span dangerouslySetInnerHTML={{ __html: link.iconSVG }} className={styles.navIconWrapper}></span>}
                <span className={styles.navLabel}>{link.label}</span>
              </RouterLink>
            )
          ))}
        </nav>
      </div>

      <div className={styles.centerSection}>
        <div className={styles.searchContainer}>
          <svg className={styles.searchIcon} viewBox="0 0 24 24" fill="currentColor">
            <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
          </svg>
          <input
            ref={searchInputRef}
            type="search"
            id="searchTitleInput"
            placeholder="Tìm kiếm theo tiêu đề..."
            className={styles.searchInput}
            value={internalSearchQuery}
            onChange={handleInternalSearchChange}
            onFocus={handleInternalSearchFocus}
            autoComplete="off"
          />
          {internalShowSuggestions && internalTitleSuggestions.length > 0 && (
            <div ref={internalSuggestionsRef} className={styles.suggestionsPanel}>
              {internalTitleSuggestions.map((suggLink) => (
                <div
                  key={suggLink.id}
                  className={styles.suggestionItem}
                  onClick={() => handleInternalSuggestionClick(suggLink.title)}
                  onMouseDown={(e) => e.preventDefault()} // Prevent input blur on click
                >
                  {suggLink.title}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className={styles.rightSection}>
        <button
          onClick={onToggleFilterPanel}
          className={`${styles.iconButton} ${styles.filterToggleButton}`}
          title="Mở/Đóng bộ lọc" // Tooltip đơn giản hơn
          dangerouslySetInnerHTML={{ __html: FILTER_ICON_SVG }}
        />
        <div className={styles.authControls}>
          {currentUser && !currentUser.isAnonymous ? (
            <>
              <span className={styles.userName}>Chào, {currentUser.displayName || currentUser.email?.split('@')[0] || 'User'}!</span>
              <button onClick={onSignOut} className={`${styles.authButton} ${styles.signOutButton}`}>Đăng xuất</button>
            </>
          ) : (
            <button onClick={onSignIn} className={`${styles.authButton} ${styles.googleSigninButton}`}>
              <span dangerouslySetInnerHTML={{ __html: GOOGLE_ICON_SVG }} className={styles.googleIcon}></span>
              Đăng nhập
            </button>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;